import "../styles/globals.css";
import "@uppy/core/dist/style.css";
import "@uppy/dashboard/dist/style.css";
import "@uppy/image-editor/dist/style.css";
import "core-js/stable";
import "regenerator-runtime/runtime";
import "simplebar-react/dist/simplebar.min.css";
import DatadogInit from "../frontend/datadog/datadog-init";
import InitUserUtils from "../frontend/init-user-utils/init-user-utils";
import { useAtomValue } from "jotai";
import AppLoader from "../frontend/loader/app-loader";
import { StateProvider } from "../frontend/state";
import { appLoadingAtom } from "../state-atoms";
import { useEffect } from "react";
import { PortalID } from "frontend/modal/portal-types";
import tracking from "frontend/tracking";
import brazeSdk from "frontend/braze-sdk";
import { DynamicModalProvider } from "frontend/modal/dynamic-modal";
import { FeatureFlagsProvider, initFeatureFlagsService } from "frontend/hooks/use-feature-flag/feature-flags-provider";
import { initMixPanelClient } from "utils/analytic-utils";
import { FRONTEND_VERSION } from "utils/version";
import { initBigbrain } from "frontend/services/bigBrainService";
import { isCIM } from "utils/url-utils";

// Init the tracking applications only on client-side
if (typeof window !== "undefined") {
  initMixPanelClient();
  brazeSdk.initBrazeClient();
  initFeatureFlagsService();
  if (isCIM()) {
    initBigbrain();
  }

  window.globalTrackAnalyticsEvent = tracking.trackAnalyticsEvent;
}

// eslint-disable-next-line react/prop-types
function MyApp({ Component, pageProps }) {
  const isLoading = useAtomValue(appLoadingAtom);

  // Define originalFetch outside to apply to first requests
  const originalFetch = typeof window === "undefined" ? null : window.fetch;

  // Override fetch if in preview environment
  if (typeof window !== "undefined" && process.env.NEXT_ENV === "preview") {
    // eslint-disable-next-line no-console
    console.log("Fetch override activated with NEXT_BRANCH_PATH:", process.env.NEXT_BRANCH_PATH);

    const originalFetch = window.fetch;
    window.fetch = async (...args) => {
      let [resource, config] = args;

      try {
        // eslint-disable-next-line no-console
        console.log("Processing request for:", resource);

        // For relative URLs, convert to absolute using window.location.origin
        let fullUrl = resource;
        if (typeof resource === "string" && resource.startsWith("/")) {
          fullUrl = window.location.origin + resource;
          // eslint-disable-next-line no-console
          console.log("Converted relative URL to:", fullUrl);
        }

        // Now parse the URL properly
        const resourceUrl = new URL(fullUrl);
        const allowedDomains = ["preview.dev.workcanvas.com", "preview-api.dev.workcanvas.com"];

        // eslint-disable-next-line no-console
        console.log("Checking domain:", resourceUrl.hostname, "against:", allowedDomains);

        if (allowedDomains.some((domain) => resourceUrl.hostname.includes(domain))) {
          // eslint-disable-next-line no-console
          console.log("Domain matched:", resourceUrl.hostname);

          // Initialize config and headers if not present
          if (!config) config = {};
          if (!config.headers) config.headers = {};

          // Add X-Branch-Path header
          config.headers["X-Branch-Path"] = `${process.env.NEXT_BRANCH_PATH || "preview"}`;

          // eslint-disable-next-line no-console
          console.log("Added X-Branch-Path header:", config.headers["X-Branch-Path"]);
        } else {
          // eslint-disable-next-line no-console
          console.log("Domain did not match allowed domains");
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error in fetch override:", error.message);
        // eslint-disable-next-line no-console
        console.error("Resource that caused error:", resource);
        // eslint-disable-next-line no-console
        console.error("Stack trace:", error.stack);
      }

      // Call the original fetch with potentially modified config
      return originalFetch(resource, config);
    };
  }

  brazeSdk.useContentCards();
  brazeSdk.useWebInAppMessages();

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.version = FRONTEND_VERSION;
    }
    // Cleanup to restore original fetch
    return () => {
      if (typeof window !== "undefined" && originalFetch) {
        window.fetch = originalFetch;
      }
    };
  }, []);

  return (
    <StateProvider>
      <DatadogInit />
      <InitUserUtils />
      <FeatureFlagsProvider>
        <DynamicModalProvider>
          <Component {...pageProps} />
          <div className={"checkout"} id={PortalID.Checkout} />
          {/* isBusy is an atom to show a dimmed backgound loading state  */}
          <div className={"curtain"} hidden={!isLoading}>
            <AppLoader />
          </div>
          {process.env.NEXT_ENV === "pr-auth" &&
            typeof window !== "undefined" &&
            !window.location.href.includes("/pr-") && <div className="preview-frame" />}
        </DynamicModalProvider>
      </FeatureFlagsProvider>
    </StateProvider>
  );
}

export default MyApp;
